import axios from 'axios';
import ENDPOINTS from '../Constants/ApiEndpoints';

// Separate axios instance for Gemini API (no withCredentials — fixes CORS)
const geminiAxios = axios.create({
  withCredentials: false
});

/**
 * Viswateja Hospital AI Engine v3
 * - Full conversation history (context memory)
 * - Gemini 2.0 Flash (free tier compatible)
 * - Function Calling with real backend APIs
 * - Smart regex fallback when Gemini is unavailable
 */

// ── Date Helpers ──
const formatDateToLocalYMD = (date) => {
  return date.getFullYear() + '-' +
    String(date.getMonth() + 1).padStart(2, '0') + '-' +
    String(date.getDate()).padStart(2, '0');
};

const resolveDateRange = (q) => {
  const today = new Date();
  if (q.includes('yesterday')) {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    const d = formatDateToLocalYMD(y);
    return { fromDate: d, toDate: d, label: 'Yesterday' };
  }
  const daysMatch = q.match(/last\s+(\d+)\s+days?/);
  if (daysMatch) {
    const days = parseInt(daysMatch[1], 10);
    const past = new Date(today); past.setDate(past.getDate() - days);
    return { fromDate: formatDateToLocalYMD(past), toDate: formatDateToLocalYMD(today), label: `Last ${days} Days` };
  }
  if (q.includes('this week') || q.includes('past 7 days') || q.includes('last week')) {
    const w = new Date(today); w.setDate(w.getDate() - 7);
    return { fromDate: formatDateToLocalYMD(w), toDate: formatDateToLocalYMD(today), label: 'This Week' };
  }
  if (q.includes('this month')) {
    const m = new Date(today.getFullYear(), today.getMonth(), 1);
    return { fromDate: formatDateToLocalYMD(m), toDate: formatDateToLocalYMD(today), label: 'This Month' };
  }
  if (q.includes('last month')) {
    const startLast = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endLast = new Date(today.getFullYear(), today.getMonth(), 0);
    return { fromDate: formatDateToLocalYMD(startLast), toDate: formatDateToLocalYMD(endLast), label: 'Last Month' };
  }
  const todayStr = formatDateToLocalYMD(today);
  return { fromDate: todayStr, toDate: todayStr, label: 'Today' };
};

// ── Gemini Function Calling Tools Schema ──
const AI_TOOLS = [
  {
    functionDeclarations: [
      {
        name: "getHospitalCollection",
        description: "Get the hospital's total financial collection/revenue. Optionally filters by invoiceType and date range. Use this when the user asks about money, revenue, collection, earnings, income, payments received.",
        parameters: {
          type: "OBJECT",
          properties: {
            fromDate: { type: "STRING", description: "Start date in YYYY-MM-DD format. Default to today." },
            toDate: { type: "STRING", description: "End date in YYYY-MM-DD format. Default to today." },
            invoiceType: { type: "STRING", description: "Optional filter: OUTPATIENT_INVOICE, INPATIENT_INVOICE, or PRESCRIPTION_INVOICE. Omit for total." }
          }
        }
      },
      {
        name: "searchPatients",
        description: "Search for patients in the hospital database by name or MR Number. Use this when the user asks about a specific patient, wants to find a patient, or mentions a name.",
        parameters: {
          type: "OBJECT",
          properties: {
            name: { type: "STRING", description: "Patient name to search for (partial match OK)" },
            mrNumber: { type: "STRING", description: "Medical Record Number e.g. VT2025001" }
          }
        }
      },
      {
        name: "getAdmissionsCount",
        description: "Get the total count of inpatient (IP) admissions for a date range. Use when user asks about admissions, IP count, admitted patients.",
        parameters: {
          type: "OBJECT",
          properties: {
            fromDate: { type: "STRING", description: "Start date YYYY-MM-DD" },
            toDate: { type: "STRING", description: "End date YYYY-MM-DD" }
          }
        }
      },
      {
        name: "getAppointmentsCount",
        description: "Get the total count of outpatient (OP) appointments for a date range. Use when user asks about appointments, OP count, outpatient visits.",
        parameters: {
          type: "OBJECT",
          properties: {
            fromDate: { type: "STRING", description: "Start date YYYY-MM-DD" },
            toDate: { type: "STRING", description: "End date YYYY-MM-DD" }
          }
        }
      },
      {
        name: "getPatientDueAmount",
        description: "Get the unpaid/due bill amount for a specific patient by their MR Number. Use when user asks about due, balance, pending amount for a patient.",
        parameters: {
          type: "OBJECT",
          properties: {
            mrNumber: { type: "STRING", description: "The patient's MR Number e.g. VT2025001" }
          },
          required: ["mrNumber"]
        }
      },
      {
        name: "getTopDuePatients",
        description: "Fetches the patients with the highest unpaid/outstanding due balances across the hospital. Use when user asks for highest due patient, top balance, or maximum unpaid bill.",
        parameters: {
          type: "OBJECT",
          properties: {
            limit: { type: "NUMBER", description: "Number of patients to return. Default 5." }
          }
        }
      },
      {
        name: "getTotalPatientsCount",
        description: "Get the total count of registered patients for a given date range. Use when user asks total patients, registered patients count.",
        parameters: {
          type: "OBJECT",
          properties: {
            fromDate: { type: "STRING", description: "Start date YYYY-MM-DD" },
            toDate: { type: "STRING", description: "End date YYYY-MM-DD" }
          }
        }
      },
      {
        name: "getDoctorAppointmentsCount",
        description: "Get the appointment count for a specific doctor by date range. Use when user asks how many patients a doctor saw.",
        parameters: {
          type: "OBJECT",
          properties: {
            doctorName: { type: "STRING", description: "Doctor's name" },
            fromDate: { type: "STRING", description: "Start date YYYY-MM-DD" },
            toDate: { type: "STRING", description: "End date YYYY-MM-DD" }
          }
        }
      },
      {
        name: "getRefundsReport",
        description: "Get the total refund amount processed for a given date range. Use when user asks about refunds, money refunded, or refund totals.",
        parameters: {
          type: "OBJECT",
          properties: {
            fromDate: { type: "STRING", description: "Start date YYYY-MM-DD" },
            toDate: { type: "STRING", description: "End date YYYY-MM-DD" }
          }
        }
      }
    ]
  }
];

// ── Tool Execution Handler ──
const executeTool = async (functionCall) => {
  const { name, args } = functionCall;
  const today = formatDateToLocalYMD(new Date());

  try {
    switch (name) {
      case "getHospitalCollection": {
        const fDate = args.fromDate || today;
        const tDate = args.toDate || today;
        const types = args.invoiceType
          ? [args.invoiceType]
          : ['OUTPATIENT_INVOICE', 'INPATIENT_INVOICE', 'PRESCRIPTION_INVOICE'];

        const results = await Promise.all(
          types.map(t =>
            axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, {
              params: { invoiceType: t, fromDate: fDate, toDate: tDate }
            }).catch(() => ({ data: 0 }))
          )
        );

        const amounts = {};
        types.forEach((t, i) => { amounts[t] = Number(results[i].data) || 0; });
        const total = Object.values(amounts).reduce((a, b) => a + b, 0);

        return {
          result: "success",
          totalRevenue: total,
          breakdown: amounts,
          currency: "INR",
          period: `${fDate} to ${tDate}`
        };
      }

      case "searchPatients": {
        const params = { page: 0, size: 50 };
        if (args.mrNumber) params.mrNumber = args.mrNumber;
        else if (args.name) params.name = args.name;

        const [opRes, ipRes] = await Promise.all([
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_APPOINTMENTS, { params }).catch(() => ({ data: { records: [] } })),
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS, { params }).catch(() => ({ data: { records: [] } }))
        ]);

        const allRecords = [...(opRes.data?.records || []), ...(ipRes.data?.records || [])];
        const unique = [];
        const seen = new Set();
        allRecords.forEach(r => {
          if (r.mrNumber && !seen.has(r.mrNumber)) {
            seen.add(r.mrNumber);
            unique.push({
              mrNumber: r.mrNumber,
              name: r.patientName || r.name || 'Unknown',
              phone: r.phoneNumber || r.phone || 'N/A',
              age: r.age,
              gender: r.gender
            });
          }
        });

        return { result: "success", patientsFound: unique.length, patients: unique.slice(0, 10) };
      }

      case "getAdmissionsCount": {
        const fDate = args.fromDate || today;
        const tDate = args.toDate || today;
        const res = await axios.get(ENDPOINTS.GET_TOTAL_IP_ADMISSIONS_COUNT_BY_DATE, {
          params: { fromDate: fDate, toDate: tDate }
        });
        return { result: "success", admissionsCount: res.data || 0, period: `${fDate} to ${tDate}` };
      }

      case "getAppointmentsCount": {
        const fDate = args.fromDate || today;
        const tDate = args.toDate || today;
        const res = await axios.get(ENDPOINTS.GET_TOTAL_OP_APPOINTMENTS_COUNT_BY_DATE, {
          params: { fromDate: fDate, toDate: tDate }
        });
        return { result: "success", appointmentsCount: res.data || 0, period: `${fDate} to ${tDate}` };
      }

      case "getPatientDueAmount": {
        if (!args.mrNumber) return { error: "mrNumber is required." };
        const res = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${args.mrNumber}`);
        return { result: "success", billClearance: res.data };
      }

      case "getTopDuePatients": {
        const [opRes, ipRes] = await Promise.all([
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_APPOINTMENTS, { params: { page: 0, size: 50 } }).catch(() => ({ data: { records: [] } })),
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS, { params: { page: 0, size: 50 } }).catch(() => ({ data: { records: [] } }))
        ]);

        const allRecords = [...(opRes.data?.records || []), ...(ipRes.data?.records || [])];
        const uniqueMap = new Map();
        allRecords.forEach(r => {
          if (r.mrNumber && !uniqueMap.has(r.mrNumber)) uniqueMap.set(r.mrNumber, r);
        });
        const matched = Array.from(uniqueMap.values());

        const dueList = [];
        for (const p of matched) {
          try {
            const bcRes = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${p.mrNumber}`);
            const bc = bcRes.data;
            const dueAmt = Number(bc?.totalDueAmount || 0);
            if (dueAmt > 0) {
              dueList.push({
                name: p.patientName || p.name || 'Patient',
                mrNumber: p.mrNumber,
                totalDueAmount: dueAmt,
                invoices: bc.invoices || []
              });
            }
          } catch (e) {}
        }

        dueList.sort((a, b) => b.totalDueAmount - a.totalDueAmount);
        return { result: "success", count: dueList.length, topDuePatients: dueList.slice(0, args.limit || 5) };
      }

      case "getTotalPatientsCount": {
        const fDate = args.fromDate || today;
        const tDate = args.toDate || today;
        const res = await axios.get(ENDPOINTS.GET_TOTAL_PATIENTS_COUNT_BY_DATE, {
          params: { fromDate: fDate, toDate: tDate }
        }).catch(() => ({ data: 0 }));
        return { result: "success", totalPatients: res.data || 0, period: `${fDate} to ${tDate}` };
      }

      case "getDoctorAppointmentsCount": {
        const fDate = args.fromDate || today;
        const tDate = args.toDate || today;
        const res = await axios.get(ENDPOINTS.GET_TOTAL_OP_APPOINTMENTS_COUNTOFDOCTOR_BYDATE, {
          params: { doctorName: args.doctorName || '', fromDate: fDate, toDate: tDate }
        }).catch(() => ({ data: 0 }));
        return { result: "success", doctorName: args.doctorName, appointmentsCount: res.data || 0, period: `${fDate} to ${tDate}` };
      }

      case "getRefundsReport": {
        const fDate = args.fromDate || today;
        const tDate = args.toDate || today;
        const res = await axios.get(ENDPOINTS.GET_OP_CHARGES_REPORT, {
          params: { fromDate: fDate, toDate: tDate }
        }).catch(() => ({ data: {} }));
        const refundTotal = Number(res.data?.totalOpChargesRefundAmount || res.data?.refundAmount || 0);
        return { result: "success", totalRefundsAmount: refundTotal, currency: "INR", period: `${fDate} to ${tDate}` };
      }

      default:
        return { error: `Unknown tool: ${name}` };
    }
  } catch (err) {
    console.error("Tool execution error:", err);
    return { error: "Failed to execute API call.", details: err.message };
  }
};

// ── Conversation History (module-level for persistence across calls) ──
let conversationHistory = [];

const SYSTEM_PROMPT = `You are **Viswateja Hospital AI Assistant** — a friendly, helpful, and knowledgeable hospital management AI.

RULES:
1. ALWAYS respond conversationally and helpfully, like a human assistant would.
2. When the user asks about hospital data (revenue, patients, admissions, appointments, due amounts), use the available tools to fetch REAL data from the backend. Never make up numbers.
3. When the user asks general questions, casual chat, medical questions, or anything NOT related to hospital database queries — answer directly from your knowledge. Be warm, friendly, and helpful.
4. Remember conversation context. If the user says "what about yesterday" or "from last 4 days" after asking about revenue, understand they want the SAME type of data for a different date range.
5. Today's date is ${formatDateToLocalYMD(new Date())}.
6. Format currency in Indian Rupees (₹) with commas.
7. Keep responses concise but informative. Use emojis sparingly for friendliness.`;

// ── Gemini API Call with Full Conversation History ──
let lastGeminiError = null; // Expose last error for diagnostics

const callGeminiWithHistory = async (userMessage) => {
  let rawKey = localStorage.getItem('VT_GEMINI_API_KEY') || process.env.REACT_APP_GEMINI_API_KEY || '';
  const apiKey = rawKey.replace(/['"`\s]/g, '').trim();
  if (!apiKey || apiKey.length < 10) {
    lastGeminiError = 'No valid API key found';
    return null;
  }

  // Strictly use Gemini 3.6 Flash as requested
  const models = ['gemini-3.6-flash'];

  // Add user message to history
  conversationHistory.push({
    role: "user",
    parts: [{ text: userMessage }]
  });

  // Keep last 20 messages to avoid token overflow
  if (conversationHistory.length > 20) {
    conversationHistory = conversationHistory.slice(-20);
  }

  for (const model of models) {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;

    try {
      // Build request with universal compatibility across all Gemini endpoints
      const requestBody = {
        contents: [
          { role: "user", parts: [{ text: `[System Context]\n${SYSTEM_PROMPT}\n[End System Context]` }] },
          ...conversationHistory
        ],
        tools: AI_TOOLS
      };

      console.log(`[AI] Trying Gemini ${model}...`);

      // Round 1: Send to Gemini
      const res1 = await geminiAxios.post(url, requestBody, { timeout: 15000 });
      const candidate = res1.data?.candidates?.[0];
      const part = candidate?.content?.parts?.[0];

      if (!part) {
        console.warn(`[AI] ${model}: No content in response`);
        continue;
      }

      console.log(`[AI] ${model} responded:`, part.functionCall ? `functionCall(${part.functionCall.name})` : 'text');

      // If Gemini wants to call a function
      if (part.functionCall) {
        const funcCall = part.functionCall;
        console.log(`[AI] Executing tool: ${funcCall.name}`, funcCall.args);

        // Execute the backend tool
        const toolResult = await executeTool(funcCall);
        console.log(`[AI] Tool result:`, toolResult);

        // Add model's complete parts (including thought_signature) to history
        conversationHistory.push({
          role: "model",
          parts: candidate.content.parts
        });

        // Add tool response to history
        conversationHistory.push({
          role: "user",
          parts: [{
            functionResponse: {
              name: funcCall.name,
              response: { name: funcCall.name, content: toolResult }
            }
          }]
        });

        // Round 2: Get Gemini's final synthesized answer
        const requestBody2 = {
          contents: [
            { role: "user", parts: [{ text: `[System Context]\n${SYSTEM_PROMPT}\n[End System Context]` }] },
            ...conversationHistory
          ],
          tools: AI_TOOLS
        };

        const res2 = await geminiAxios.post(url, requestBody2, { timeout: 15000 });
        const finalText = res2.data?.candidates?.[0]?.content?.parts?.[0]?.text;

        if (finalText) {
          conversationHistory.push({ role: "model", parts: [{ text: finalText }] });
          lastGeminiError = null;
          return finalText;
        }
      }

      // If Gemini returned plain text (no function call)
      if (part.text) {
        conversationHistory.push({ role: "model", parts: [{ text: part.text }] });
        lastGeminiError = null;
        return part.text;
      }

      conversationHistory.pop();
      continue;

    } catch (err) {
      const errMsg = err?.response?.data?.error?.message || err?.message || 'Unknown error';
      console.error(`[AI] Gemini ${model} FAILED:`, errMsg);
      lastGeminiError = `${model}: ${errMsg}`;
      continue;
    }
  }

  // All models failed — pop the user message we added so history stays clean
  if (conversationHistory.length > 0 && conversationHistory[conversationHistory.length - 1].role === 'user') {
    conversationHistory.pop();
  }

  return null; // All models failed
};

// ── Main Exported Processor ──
export const processAiQuery = async (queryText, lastContext = null) => {
  if (!queryText || !queryText.trim()) {
    return { text: "Please ask any hospital, clinical, or patient question in English.", type: "warning" };
  }

  const rawQ = queryText.trim();
  const q = rawQ.toLowerCase().replace(/[?.,!/\\;:'"()-]/g, ' ').replace(/\s+/g, ' ').trim();
  const dateRange = resolveDateRange(q);

  // ── 1. Try Gemini with full conversation history ──
  const apiKey = localStorage.getItem('VT_GEMINI_API_KEY') || process.env.REACT_APP_GEMINI_API_KEY;
  if (apiKey) {
    const geminiResponse = await callGeminiWithHistory(rawQ);
    if (geminiResponse) {
      return { text: geminiResponse, type: 'llm_response' };
    }
    // If Gemini failed, log it but continue to fallback
    if (lastGeminiError) {
      console.warn('[AI] Gemini failed, using fallback. Error:', lastGeminiError);
    }
  }

  // ── 2. Smart Regex Fallback (when Gemini is unavailable) ──
  // Determine intent
  let effectiveIntent = null;
  const isPaginationFollowup = q.includes('next') || q.includes('more') || q.includes('page 2') || q.includes('after that');
  const isPureDateFollowup = (q === 'yesterday' || q === 'today' || q === 'this month' || q === 'last month' ||
    q === 'this week' || q.startsWith('what about ') || q.startsWith('how about ') ||
    /^(from\s+)?last\s+\d+\s+days?$/.test(q));

  if (isPaginationFollowup && lastContext?.intent) {
    effectiveIntent = lastContext.intent;
  } else if (q.includes('refund')) {
    effectiveIntent = 'REFUNDS';
  } else if (q.includes('due') || q.includes('balance') || q.includes('unpaid') || q.includes('pending')) {
    effectiveIntent = 'DUE_AMOUNT';
  } else if (q.includes('collection') || q.includes('revenue') || q.includes('earning') || q.includes('income') || q.includes('payment') || q.includes('upi') || q.includes('cash') || q.includes('card') || q.includes('online') || q.includes('mode') || q.includes('netbanking') || q.includes('gpay') || q.includes('phonepe')) {
    effectiveIntent = 'COLLECTION';
  } else if (q.includes('admit') || q.includes('admission') || q.includes('ip count') || q.includes('inpatient')) {
    effectiveIntent = 'ADMISSIONS';
  } else if (q.includes('appointment') || q.includes('op count') || q.includes('outpatient')) {
    effectiveIntent = 'APPOINTMENTS';
  } else if (q.includes('prescription') || q.includes('pharmacy')) {
    effectiveIntent = 'PRESCRIPTIONS';
  } else if (q.includes('total patient') || q.includes('all patient') || q.includes('how many patient')) {
    effectiveIntent = 'TOTAL_PATIENTS';
  } else if (q.includes('patient') || q.includes('find') || q.includes('search') || q.match(/vt\d+/i)) {
    effectiveIntent = 'PATIENT_SEARCH';
  } else if (isPureDateFollowup && lastContext?.intent) {
    effectiveIntent = lastContext.intent;
  }

  // ── Handle REFUNDS ──
  if (effectiveIntent === 'REFUNDS') {
    try {
      const res = await axios.get(ENDPOINTS.GET_OP_CHARGES_REPORT, {
        params: { fromDate: dateRange.fromDate, toDate: dateRange.toDate }
      });
      const refundTotal = Number(res.data?.totalOpChargesRefundAmount || res.data?.refundAmount || 0);
      return {
        text: `💸 **Hospital Refund Report (${dateRange.label}):**\n\n- **Total Refunds Processed**: **₹ ${refundTotal.toLocaleString('en-IN')}**`,
        type: 'refunds',
        context: { intent: 'REFUNDS', dateRange }
      };
    } catch (e) {
      return {
        text: `💸 **Hospital Refund Report (${dateRange.label}):**\n\n- **Total Refunds Processed**: **₹ 0**`,
        type: 'refunds',
        context: { intent: 'REFUNDS', dateRange }
      };
    }
  }

  // ── Handle DUE_AMOUNT with patient name, top/highest, or total calculate query ──
  if (effectiveIntent === 'DUE_AMOUNT') {
    const isTotalHospitalDueQuery = q.includes('total') || q.includes('all') || q.includes('calculat') || q.includes('sum') || q.includes('overall') || q.includes('hospital');
    const isHighestQuery = isPaginationFollowup || q.includes('highest') || q.includes('top') || q.includes('max') || q.includes('most') || q.includes('largest');

    // Case A: Calculate Total Hospital Dues
    if (isTotalHospitalDueQuery && !q.match(/(?:for|of|from)\s+[a-z]+/)) {
      try {
        const [opRes, ipRes] = await Promise.all([
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_APPOINTMENTS, { params: { page: 0, size: 100 } }).catch(() => ({ data: { records: [] } })),
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS, { params: { page: 0, size: 100 } }).catch(() => ({ data: { records: [] } }))
        ]);

        const allRecords = [...(opRes.data?.records || []), ...(ipRes.data?.records || [])];
        const uniqueMap = new Map();
        allRecords.forEach(r => {
          if (r.mrNumber && !uniqueMap.has(r.mrNumber)) uniqueMap.set(r.mrNumber, r);
        });
        const matched = Array.from(uniqueMap.values());

        let totalHospitalDue = 0;
        const duePatients = [];

        for (const p of matched) {
          try {
            const bcRes = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${p.mrNumber}`);
            const bc = bcRes.data;
            const dueAmt = Number(bc?.totalDueAmount || 0);
            if (dueAmt > 0) {
              totalHospitalDue += dueAmt;
              duePatients.push({
                name: p.patientName || p.name || 'Patient',
                mrNumber: p.mrNumber,
                dueAmt
              });
            }
          } catch (e) {}
        }

        duePatients.sort((a, b) => b.dueAmt - a.dueAmt);

        const topBreakdown = duePatients.slice(0, 3).map(p =>
          `• **${p.name}** (\`${p.mrNumber}\`): ₹ ${p.dueAmt.toLocaleString('en-IN')}`
        ).join('\n');

        return {
          text: `💳 **Total Hospital Outstanding Due Calculation:**\n\n- **Total Outstanding Due**: **₹ ${totalHospitalDue.toLocaleString('en-IN')}**\n- **Patients with Pending Dues**: **${duePatients.length}** patient(s)\n\n**Top Pending Accounts:**\n${topBreakdown || 'None'}`,
          type: 'due_summary',
          context: { intent: 'DUE_AMOUNT' }
        };
      } catch (e) {
        console.error('Total due calculation error:', e);
      }
    }

    // Case B: Highest / Top Due Patients or Pagination
    if (isHighestQuery) {
      try {
        const offset = isPaginationFollowup ? ((lastContext?.page || 0) + 5) : 0;

        const [opRes, ipRes] = await Promise.all([
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_APPOINTMENTS, { params: { page: 0, size: 100 } }).catch(() => ({ data: { records: [] } })),
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS, { params: { page: 0, size: 100 } }).catch(() => ({ data: { records: [] } }))
        ]);

        const allRecords = [...(opRes.data?.records || []), ...(ipRes.data?.records || [])];
        const uniqueMap = new Map();
        allRecords.forEach(r => {
          if (r.mrNumber && !uniqueMap.has(r.mrNumber)) uniqueMap.set(r.mrNumber, r);
        });
        const matched = Array.from(uniqueMap.values());

        const dueList = [];
        for (const p of matched) {
          try {
            const bcRes = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${p.mrNumber}`);
            const bc = bcRes.data;
            const dueAmt = Number(bc?.totalDueAmount || 0);
            if (dueAmt > 0) {
              dueList.push({
                name: p.patientName || p.name || 'Patient',
                mrNumber: p.mrNumber,
                dueAmt,
                invoices: bc.invoices || []
              });
            }
          } catch (e) {}
        }

        dueList.sort((a, b) => b.dueAmt - a.dueAmt);

        if (dueList.length === 0) {
          return { text: `✅ **No Outstanding Dues Found!** All recent patient bills are fully cleared.`, type: 'info' };
        }

        const pageSlice = dueList.slice(offset, offset + 5);

        if (pageSlice.length === 0) {
          return { text: `ℹ️ **No More Patients:** Reached the end of outstanding due records (Total: ${dueList.length} patient(s)).`, type: 'info' };
        }

        const formattedList = pageSlice.map((d, idx) => {
          const pendingTypes = d.invoices.length > 0
            ? [...new Set(d.invoices.map(i => i.invoiceType?.replace('_INVOICE', '').toLowerCase()))].join(', ')
            : 'Unpaid';
          return `${offset + idx + 1}. **${d.name}** (\`${d.mrNumber}\`)\n   💰 Outstanding Due: **₹ ${d.dueAmt.toLocaleString('en-IN')}** (${pendingTypes})`;
        }).join('\n\n');

        const titleHeader = offset > 0 ? `🏆 **Next Highest Due Patients (${offset + 1} - ${offset + pageSlice.length}):**` : `🏆 **Highest Outstanding Due Patient(s):**`;

        return {
          text: `${titleHeader}\n\n${formattedList}`,
          type: 'due_results',
          context: { intent: 'DUE_AMOUNT', page: offset }
        };
      } catch (e) {
        console.error('Highest due lookup error:', e);
      }
    }

    // Extract patient name from query like "due amount for param", "param due amount", "balance of ramesh"
    let patientName = null;
    const nameMatch = q.match(/(?:due|balance|unpaid|pending)(?:\s+amount)?\s+(?:for|of|from)\s+(.+)/);
    if (nameMatch) {
      patientName = nameMatch[1].trim();
    } else {
      const cleaned = q
        .replace(/\bhighest\b/gi, '')
        .replace(/\btop\b/gi, '')
        .replace(/\bmax\b/gi, '')
        .replace(/\bmaximum\b/gi, '')
        .replace(/\bmost\b/gi, '')
        .replace(/\blargest\b/gi, '')
        .replace(/\bdue\b/gi, '')
        .replace(/\bamount\b/gi, '')
        .replace(/\bbalance\b/gi, '')
        .replace(/\bunpaid\b/gi, '')
        .replace(/\bpending\b/gi, '')
        .replace(/\bpatient\b/gi, '')
        .replace(/\bpatients\b/gi, '')
        .replace(/\bwhat\b/gi, '')
        .replace(/\bis\b/gi, '')
        .replace(/\bthe\b/gi, '')
        .replace(/\bfor\b/gi, '')
        .replace(/\bof\b/gi, '')
        .replace(/\bfrom\b/gi, '')
        .replace(/\bany\b/gi, '')
        .replace(/\bshow\b/gi, '')
        .replace(/\bme\b/gi, '')
        .replace(/\bget\b/gi, '')
        .replace(/\bcheck\b/gi, '')
        .replace(/\bfind\b/gi, '')
        .trim();
      if (cleaned.length > 1) {
        patientName = cleaned;
      }
    }

    if (patientName && patientName.length > 1) {
      try {
        // Step 1: Search for the patient by name
        const params = { page: 0, size: 50, name: patientName };
        const [opRes, ipRes] = await Promise.all([
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_APPOINTMENTS, { params }).catch(() => ({ data: { records: [] } })),
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS, { params }).catch(() => ({ data: { records: [] } }))
        ]);

        const allRecords = [...(opRes.data?.records || []), ...(ipRes.data?.records || [])];
        const uniqueMap = new Map();
        allRecords.forEach(r => {
          if (r.mrNumber && !uniqueMap.has(r.mrNumber)) uniqueMap.set(r.mrNumber, r);
        });
        const matched = Array.from(uniqueMap.values());

        if (matched.length === 0) {
          return { text: `🔍 No patient named **"${patientName}"** found in the database.`, type: 'warning' };
        }

        // Step 2: For each matched patient, call getBillClearance
        const dueResults = [];
        for (const p of matched.slice(0, 5)) {
          try {
            const bcRes = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${p.mrNumber}`);
            const bc = bcRes.data;
            dueResults.push({
              name: p.patientName || p.name || 'Patient',
              mrNumber: p.mrNumber,
              billClearance: bc
            });
          } catch (e) {
            dueResults.push({
              name: p.patientName || p.name || 'Patient',
              mrNumber: p.mrNumber,
              billClearance: null
            });
          }
        }

        const lines = dueResults.map(d => {
          const bc = d.billClearance;
          if (bc) {
            const dueAmt = bc.totalDueAmount !== undefined && bc.totalDueAmount !== null
              ? bc.totalDueAmount
              : (bc.dueAmount || bc.totalDue || 0);
            
            const pendingTypes = bc.invoices && bc.invoices.length > 0
              ? [...new Set(bc.invoices.map(i => i.invoiceType?.replace('_INVOICE', '').toLowerCase()))].join(', ')
              : null;

            const statusStr = dueAmt > 0 ? `⚠️ Pending (${pendingTypes || 'Unpaid'})` : '✅ Fully Cleared';

            return `• **${d.name}** (\`${d.mrNumber}\`)\n  💰 Outstanding Due: **₹ ${Number(dueAmt).toLocaleString('en-IN')}** | ${statusStr}`;
          }
          return `• **${d.name}** (\`${d.mrNumber}\`) — Due info unavailable`;
        }).join('\n\n');

        return {
          text: `💳 **Due Amount for "${patientName}":**\n\n${lines}`,
          type: 'due_results',
          context: { intent: 'DUE_AMOUNT' }
        };
      } catch (e) {
        console.error('Due amount lookup error:', e);
      }
    }

    return {
      text: `💳 **Due Amount Lookup:**\n\nPlease specify a patient name, e.g.:\n• *"due amount for Param"*\n• *"balance of Ramesh"*\n\nOr check the **Central Billing** tab for the full list of unpaid invoices.`,
      type: 'info',
      context: { intent: 'DUE_AMOUNT' }
    };
  }

  // Casual / greeting
  if (q.includes('how r u') || q.includes('how are you') || q.includes('how are u')) {
    return { text: `😊 I'm doing great, thank you for asking! How can I help you with the hospital today?`, type: 'chat' };
  }
  if (q === 'hi' || q === 'hello' || q.startsWith('hi ') || q.startsWith('hello ')) {
    return { text: `👋 Hello! I'm **Viswateja Hospital AI Assistant**. Ask me anything — patient info, revenue, admissions, or even medical guidance!`, type: 'greeting' };
  }

  // Execute intents
  if (effectiveIntent === 'COLLECTION') {
    try {
      const [opRes, ipRes, pharmRes] = await Promise.all([
        axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, { params: { invoiceType: 'OUTPATIENT_INVOICE', fromDate: dateRange.fromDate, toDate: dateRange.toDate } }).catch(() => ({ data: 0 })),
        axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, { params: { invoiceType: 'INPATIENT_INVOICE', fromDate: dateRange.fromDate, toDate: dateRange.toDate } }).catch(() => ({ data: 0 })),
        axios.get(ENDPOINTS.GET_TOTAL_COLLECTION, { params: { invoiceType: 'PRESCRIPTION_INVOICE', fromDate: dateRange.fromDate, toDate: dateRange.toDate } }).catch(() => ({ data: 0 })),
      ]);
      const opAmt = Number(opRes.data) || 0;
      const ipAmt = Number(ipRes.data) || 0;
      const pharmAmt = Number(pharmRes.data) || 0;
      const totalAmt = opAmt + ipAmt + pharmAmt;
      return {
        text: `💳 **Hospital Financial Overview (${dateRange.label}):**\n\n- **Total Revenue**: **₹ ${totalAmt.toLocaleString('en-IN')}**\n- **OP Collection**: ₹ ${opAmt.toLocaleString('en-IN')}\n- **IP Collection**: ₹ ${ipAmt.toLocaleString('en-IN')}\n- **Pharmacy**: ₹ ${pharmAmt.toLocaleString('en-IN')}`,
        type: 'revenue',
        context: { intent: 'COLLECTION' }
      };
    } catch (e) { /* fall through */ }
  }

  if (effectiveIntent === 'ADMISSIONS') {
    try {
      const res = await axios.get(ENDPOINTS.GET_TOTAL_IP_ADMISSIONS_COUNT_BY_DATE, { params: { fromDate: dateRange.fromDate, toDate: dateRange.toDate } });
      return {
        text: `🏥 **Total IP Admissions (${dateRange.label}):** ${res.data || 0} Patient(s)`,
        type: 'stats',
        context: { intent: 'ADMISSIONS' }
      };
    } catch (e) { /* fall through */ }
  }

  if (effectiveIntent === 'APPOINTMENTS') {
    try {
      const res = await axios.get(ENDPOINTS.GET_TOTAL_OP_APPOINTMENTS_COUNT_BY_DATE, { params: { fromDate: dateRange.fromDate, toDate: dateRange.toDate } });
      return {
        text: `🩺 **Total OP Appointments (${dateRange.label}):** ${res.data || 0} Patient(s)`,
        type: 'stats',
        context: { intent: 'APPOINTMENTS' }
      };
    } catch (e) { /* fall through */ }
  }

  if (effectiveIntent === 'PRESCRIPTIONS') {
    try {
      const res = await axios.get(ENDPOINTS.GET_TOTAL_PRESCRIPTIONS_COUNT_BY_DATE, { params: { fromDate: dateRange.fromDate, toDate: dateRange.toDate } });
      return {
        text: `💊 **Total Prescriptions (${dateRange.label}):** ${res.data || 0}`,
        type: 'stats',
        context: { intent: 'PRESCRIPTIONS' }
      };
    } catch (e) { /* fall through */ }
  }

  // Patient search fallback
  const isPatientQuery = q.includes('find') || q.includes('search') || q.includes('patient') || q.match(/vt\d+/i);
  if (isPatientQuery) {
    const mrMatch = q.match(/vt\d+/i);
    const mrNumber = mrMatch ? mrMatch[0].toUpperCase() : null;
    const keyword = q.replace(/(is\s+there\s+any|do\s+we\s+have|show\s+me|any|named|called|find|patient|search|look\s*up|mr\s*no|mr)/gi, '').trim();

    if (mrNumber || keyword.length > 2) {
      try {
        const params = { page: 0, size: 50 };
        if (mrNumber) params.mrNumber = mrNumber;
        else params.name = keyword;

        const [opRes, ipRes] = await Promise.all([
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_APPOINTMENTS, { params }).catch(() => ({ data: { records: [] } })),
          axios.get(ENDPOINTS.GET_ALL_PATIENTS_WITH_ADMISSIONS, { params }).catch(() => ({ data: { records: [] } }))
        ]);

        const allRecords = [...(opRes.data?.records || []), ...(ipRes.data?.records || [])];
        const uniqueMap = new Map();
        allRecords.forEach(r => {
          if (r.mrNumber && !uniqueMap.has(r.mrNumber)) uniqueMap.set(r.mrNumber, r);
        });
        const matched = Array.from(uniqueMap.values());

        if (matched.length > 0) {
          const pList = matched.slice(0, 5).map(r =>
            `• **${r.patientName || r.name || 'Patient'}** (MR: \`${r.mrNumber}\`) — Phone: ${r.phoneNumber || r.phone || 'N/A'}`
          ).join('\n');
          return { text: `🔍 **Found ${matched.length} patient(s):**\n\n${pList}`, type: 'patient_results' };
        }
      } catch (e) { /* fall through */ }
    }
    return { text: `🔍 No patient found matching your query.`, type: 'warning' };
  }

  if (apiKey) {
    return {
      text: `🤖 **Viswateja AI Assistant:**\n\nI couldn't find a direct database match for **"${rawQ}"**.\n\n💡 **Try asking:**\n• *"calculate total due"* or *"due amount for [name]"*\n• *"highest due patient"* / *"next 5 patients"*\n• *"how much collection today?"*\n• *"total admissions / appointments today"*`,
      type: 'help'
    };
  }

  return {
    text: `🤖 **Viswateja AI Assistant:**\n\nI processed your question: **"${rawQ}"**.\n\n*Note: To unlock full dynamic AI answers, add a Gemini API Key using the 🔑 icon in the header!*`,
    type: 'help'
  };
};

// ── Reset conversation (can be called from widget) ──
export const resetConversation = () => {
  conversationHistory = [];
};
